/**
 * Petits morceaux communs aux écrans « Logiciels connectés » : blocs de
 * formulaire, liste d'agents à cocher, secret à copier, signature des requêtes.
 */
import { useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Check, Copy } from 'lucide-react'
import type { AIAgent } from '@/api/aiAgents'
import { useNewSigningSecret } from '@/api/apiActions'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'

export const erreurServeur = (e: unknown) => (e as { response?: { data?: { error?: string } } })?.response?.data?.error

export function Bloc({ titre, aide, children }: { titre: string; aide?: string; children: ReactNode }) {
  return (
    <div className="space-y-3 py-5 border-b border-slate-100 dark:border-slate-800 last:border-0">
      <div>
        <h4 className="text-sm font-semibold text-slate-900 dark:text-white">{titre}</h4>
        {aide && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{aide}</p>}
      </div>
      {children}
    </div>
  )
}

export function Ligne({ label, aide, children }: { label: string; aide?: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-slate-600 dark:text-slate-300">{label}</Label>
      {children}
      {aide && <p className="text-xs text-slate-500 dark:text-slate-400">{aide}</p>}
    </div>
  )
}

/** Les agents IA du compte, à cocher. */
export function AgentChecklist({ agents, value, onChange }: { agents: AIAgent[]; value: number[]; onChange: (ids: number[]) => void }) {
  const { t } = useTranslation()
  if (!agents.length) {
    return <p className="text-sm text-slate-500 dark:text-slate-400">{t('manager.connected.noAgents', 'Aucun agent IA pour l’instant.')}</p>
  }
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
      {agents.map((a) => (
        <label key={a.id} className="flex items-center gap-3 px-3 py-2.5 cursor-pointer">
          <input type="checkbox" className="h-4 w-4" checked={value.includes(a.id)}
            onChange={(e) => onChange(e.target.checked ? [...value, a.id] : value.filter((x) => x !== a.id))} />
          <span className="text-sm text-slate-900 dark:text-white">{a.name}</span>
          {a.phone_number_display && <span className="text-xs text-slate-500 dark:text-slate-400">{a.phone_number_display}</span>}
        </label>
      ))}
    </div>
  )
}

/** Un secret montré une seule fois, avec un bouton Copier. */
export function SecretACopier({ secret }: { secret: string }) {
  const { t } = useTranslation()
  const [copie, setCopie] = useState(false)
  const copier = async () => {
    try {
      await navigator.clipboard.writeText(secret)
      setCopie(true)
      setTimeout(() => setCopie(false), 2000)
    } catch {
      setCopie(false)
    }
  }
  return (
    <div className="flex items-stretch gap-2">
      {/* Jamais filmé par PostHog ni montré pendant un partage d'écran. */}
      <code data-masque className="ph-no-capture flex-1 min-w-0 break-all text-xs bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-md px-3 py-2">{secret}</code>
      <button type="button" onClick={copier}
        className="inline-flex items-center gap-1.5 shrink-0 rounded-md border border-slate-200 dark:border-slate-700 px-3 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800">
        {copie ? <Check className="h-4 w-4 text-slate-500" /> : <Copy className="h-4 w-4 text-slate-500" />}
        {copie ? t('manager.connected.copied', 'Copié') : t('manager.connected.copy', 'Copier')}
      </button>
    </div>
  )
}

/** Réglages avancés d'une connexion enregistrée : Callizy signe chaque requête ;
 *  pour que le logiciel le vérifie, on crée un secret et on le lui donne. Le
 *  secret ne s'affiche qu'une fois ; en créer un autre remplace l'ancien, avec
 *  une confirmation dans la page si un secret a déjà été donné. */
export function SignatureSection({ companyId, connexionId, dejaDonne }: { companyId: number | undefined; connexionId: number; dejaDonne: boolean }) {
  const { t } = useTranslation()
  const creer = useNewSigningSecret(companyId)
  const [secret, setSecret] = useState<string | null>(null)
  const [donne, setDonne] = useState(dejaDonne)
  const [confirmer, setConfirmer] = useState(false)

  const lancer = async () => {
    setConfirmer(false)
    try {
      setSecret((await creer.mutateAsync(connexionId)).signing_secret)
      setDonne(true)
    } catch (e) {
      toast.error(erreurServeur(e) || t('manager.connected.errSave', 'Impossible d’enregistrer'))
    }
  }

  return (
    <Bloc titre={t('manager.connected.signTitle', 'Signature des requêtes (pour les développeurs)')}
      aide={t('manager.connected.signHelp', 'Callizy signe chaque requête. Pour que votre logiciel le vérifie, créez un secret et donnez-le lui.')}>
      {secret ? (
        <div className="space-y-2">
          <SecretACopier secret={secret} />
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {t('manager.connected.signOnce', 'Il ne sera plus affiché.')}
            <br />
            {t('manager.connected.signReplace', 'Créer un nouveau secret remplace l’ancien.')}
          </p>
        </div>
      ) : confirmer ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-3">
          <p className="text-sm text-slate-700 dark:text-slate-200">
            {t('manager.connected.signConfirm', 'Remplacer le secret actuel ?')}
            <br />
            <span className="text-xs text-slate-500 dark:text-slate-400">{t('manager.connected.signConfirmHelp', 'Votre logiciel devra recevoir le nouveau.')}</span>
          </p>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setConfirmer(false)}>{t('manager.connected.cancel', 'Annuler')}</Button>
            <Button type="button" size="sm" disabled={creer.isPending} onClick={lancer}>{t('manager.connected.signReplaceButton', 'Remplacer')}</Button>
          </div>
        </div>
      ) : (
        <Button type="button" variant="outline" size="sm" disabled={creer.isPending} onClick={() => (donne ? setConfirmer(true) : lancer())}>
          {t('manager.connected.signCreate', 'Créer un secret')}
        </Button>
      )}
    </Bloc>
  )
}
